import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Button, Card, Row, Col, message, Space, Typography,
  Checkbox, Divider, Descriptions, Tag, Spin, Modal,
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, FileTextOutlined, DownloadOutlined,
  EyeOutlined, CaretRightOutlined, CaretDownOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Title, Text } = Typography;
const { TextArea } = Input;

const KiraSozlesmeForm = () => {
  const [form] = Form.useForm();
  const [kiralikData, setKiralikData] = useState(null);
  const [maddelerState, setMaddelerState] = useState([]); // { id, maddeBaslik, maddeIcerik, siraNo, selected, customIcerik, expanded }
  const [existingSozlesme, setExistingSozlesme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { kiralikId } = useParams();
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kiralikRes, maddelerRes, sozlesmeRes] = await Promise.allSettled([
        api.get(`/kiralik/${kiralikId}`),
        api.get('/sozlesme/maddeler'),
        api.get(`/sozlesme/kiralik/${kiralikId}`),
      ]);

      // Standart maddeler (yeni sözleşme için)
      let standartMaddeler = [];
      if (maddelerRes.status === 'fulfilled') {
        const arr = Array.isArray(maddelerRes.value.data) ? maddelerRes.value.data : maddelerRes.value.data.data || [];
        standartMaddeler = arr;
      }

      if (kiralikRes.status === 'fulfilled') {
        setKiralikData(kiralikRes.value.data.data || kiralikRes.value.data);
      }

      // Mevcut sözleşme varsa detay tablosundan yükle (C# mantığı)
      if (sozlesmeRes.status === 'fulfilled' && sozlesmeRes.value.data) {
        const raw = sozlesmeRes.value.data;
        const sozlesme = Array.isArray(raw) ? raw[0] : (raw.data || raw);

        if (sozlesme && sozlesme.id) {
          setExistingSozlesme(sozlesme);
          form.setFieldsValue({
            sozlesmeNo: sozlesme.sozlesmeNo,
            ekMaddeler: sozlesme.ekMaddeler,
          });

          // Eski sözleşme: TÜM standart maddeler gösterilir,
          // detay tablosunda olanlar seçili+özel içerikli, olmayanlar seçisiz
          try {
            const detayRes = await api.get(`/sozlesme/${sozlesme.id}/maddeler`);
            const detayArr = Array.isArray(detayRes.data) ? detayRes.data : detayRes.data.data || [];

            // Kaydedilmiş maddeleri id'ye göre map'e al
            const savedMap = {};
            detayArr.forEach((d) => {
              const key = d.maddeId || d.id;
              if (key) savedMap[key] = d;
            });

            // Standart maddelerden yola çık — kaydedilmişse seçili+özel içerik, değilse seçisiz
            const stateArr = standartMaddeler.map((m) => {
              const saved = savedMap[m.id];
              return {
                id: m.id,
                maddeBaslik: m.maddeBaslik,
                maddeIcerik: m.maddeIcerik,
                siraNo: m.siraNo,
                selected: !!saved,
                customIcerik: saved ? saved.maddeIcerik : m.maddeIcerik,
                expanded: false,
              };
            });
            setMaddelerState(stateArr);
          } catch {
            setMaddelerState(standartMaddeler.map((m) => ({
              id: m.id,
              maddeBaslik: m.maddeBaslik,
              maddeIcerik: m.maddeIcerik,
              siraNo: m.siraNo,
              selected: true,
              customIcerik: m.maddeIcerik,
              expanded: false,
            })));
          }
          setLoading(false);
          if (!form.getFieldValue('sozlesmeNo')) {
            const year = dayjs().year();
            form.setFieldValue('sozlesmeNo', `KS-${year}-${String(kiralikId).padStart(3, '0')}`);
          }
          return; // Mevcut sözleşme yüklendi, çık
        }
      }

      // Yeni sözleşme: standart maddelerden yükle, hepsi seçili
      const stateArr = standartMaddeler.map((m) => ({
        id: m.id,
        maddeBaslik: m.maddeBaslik,
        maddeIcerik: m.maddeIcerik,
        siraNo: m.siraNo,
        selected: true,
        customIcerik: m.maddeIcerik,
        expanded: false,
      }));
      setMaddelerState(stateArr);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }

    // Yeni sözleşme no oluştur
    if (!form.getFieldValue('sozlesmeNo')) {
      const year = dayjs().year();
      form.setFieldValue('sozlesmeNo', `KS-${year}-${String(kiralikId).padStart(3, '0')}`);
    }
  }, [kiralikId, form]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSelect = (id) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, selected: !m.selected } : m)
    );
  };

  const toggleExpand = (id) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, expanded: !m.expanded } : m)
    );
  };

  const updateCustomIcerik = (id, value) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, customIcerik: value } : m)
    );
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const selectedMaddeler = maddelerState.filter((m) => m.selected);
      const maddelerIcerik = selectedMaddeler.map((m) => ({
        maddeId: m.id,
        maddeBaslik: m.maddeBaslik,
        maddeIcerik: m.customIcerik || m.maddeIcerik,
        siraNo: m.siraNo,
      }));

      const payload = {
        kiralikId: parseInt(kiralikId),
        sozlesmeNo: values.sozlesmeNo,
        ekMaddeler: values.ekMaddeler,
        seciliMaddeler: selectedMaddeler.map((m) => m.id).join(','),
        maddelerIcerik,
      };

      if (existingSozlesme) {
        await api.put(`/sozlesme/${existingSozlesme.id}`, payload);
        message.success('Sözleşme güncellendi');
      } else {
        await api.post('/sozlesme', payload);
        message.success('Sözleşme oluşturuldu');
      }
      navigate('/kira-sozlesmesi');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const downloadBlob = (blobData, headers) => {
    const cd = headers['content-disposition'] || '';
    const match = cd.match(/filename\*?=(?:UTF-8'')?([^;\r\n]+)/i);
    const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : `KiraSozlesmesi_${kiralikId}.docx`;
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleBlobError = async (err, fallback) => {
    let errMsg = fallback;
    try {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        errMsg = parsed.error || fallback;
      } else if (err.response?.data?.error) {
        errMsg = err.response.data.error;
      }
    } catch {}
    message.error(errMsg);
  };

  const handleWordIndir = async () => {
    setWordLoading(true);
    try {
      const response = await api.get(`/sozlesme/generate-word/${kiralikId}`, { responseType: 'blob' });
      downloadBlob(response.data, response.headers);
    } catch (err) {
      await handleBlobError(err, 'Word belgesi oluşturulamadı');
    } finally {
      setWordLoading(false);
    }
  };

  const handleAc = async () => {
    setAcLoading(true);
    try {
      const response = await api.get(`/sozlesme/saved-word/${kiralikId}`, { responseType: 'blob' });
      downloadBlob(response.data, response.headers);
    } catch (err) {
      await handleBlobError(err, 'Kaydedilmiş dosya bulunamadı. Önce "Kaydet ve İndir" yapınız.');
    } finally {
      setAcLoading(false);
    }
  };

  const selectedMaddeler = maddelerState.filter((m) => m.selected);

  const turkceYaz = (sayi) => {
    if (!sayi && sayi !== 0) return '';
    sayi = Math.floor(Number(sayi));
    if (isNaN(sayi) || sayi < 0) return '';
    if (sayi === 0) return 'SIFIR';
    const birler = ['', 'BİR', 'İKİ', 'ÜÇ', 'DÖRT', 'BEŞ', 'ALTI', 'YEDİ', 'SEKİZ', 'DOKUZ'];
    const onlar = ['', 'ON', 'YİRMİ', 'OTUZ', 'KIRK', 'ELLİ', 'ALTMIŞ', 'YETMİŞ', 'SEKSEN', 'DOKSAN'];
    const ucHane = (n) => {
      const p = [];
      const y = Math.floor(n / 100), o = Math.floor((n % 100) / 10), b = n % 10;
      if (y === 1) p.push('YÜZ'); else if (y > 1) p.push(birler[y] + 'YÜZ');
      if (o > 0) p.push(onlar[o]);
      if (b > 0) p.push(birler[b]);
      return p.join('');
    };
    const parts = [];
    const milyar = Math.floor(sayi / 1000000000);
    const milyon = Math.floor((sayi % 1000000000) / 1000000);
    const bin = Math.floor((sayi % 1000000) / 1000);
    const kalan = sayi % 1000;
    if (milyar > 0) parts.push(ucHane(milyar) + 'MİLYAR');
    if (milyon > 0) parts.push(ucHane(milyon) + 'MİLYON');
    if (bin > 0) parts.push(bin === 1 ? 'BİN' : ucHane(bin) + 'BİN');
    if (kalan > 0) parts.push(ucHane(kalan));
    return parts.join('');
  };

  const buildKiraValueMap = (data) => {
    if (!data) return {};
    const fmt = (v) => v ? Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '0,00';
    const fmtDate = (v) => v ? dayjs(v).format('DD.MM.YYYY') : '';
    return {
      IL: data.ilAdi || '',
      ILCE: data.ilceAdi || '',
      ADRES: data.adres || '',
      KIRAYA_VEREN: data.kirayaVeren || '',
      KIRAYA_VEREN_TC: data.kirayaVerenTc || '',
      KIRAYA_VEREN_TEL: data.kirayaVerenTel || '',
      KIRALAYAN: data.kiralayanAdi || '',
      KIRALAYAN_TC: data.kiralayanTc || '',
      KIRALAYAN_TEL: data.kiralayanTel || '',
      YILLIK_TUTAR: fmt(data.yillikTutar),
      YILLIK_TUTAR_YAZI: turkceYaz(data.yillikTutar),
      AYLIK_TUTAR: fmt(data.aylikTutar),
      AYLIK_TUTAR_YAZI: turkceYaz(data.aylikTutar),
      ODEME_SEKLI: data.odemeSekli || '',
      IBAN: data.ibanNo || data.iban || '',
      KIRA_BASLANGIC: fmtDate(data.kiraTarihi),
      KIRA_BITIS: fmtDate(data.kiraBitisTarihi),
      KIRA_AMAC: data.kiraAmac || 'Mesken',
      EMLAKTIPI: data.emlakTipiAdi || '',
      KIRA_SURESI: data.kiraSure || '',
      TARIH: dayjs().format('DD.MM.YYYY'),
    };
  };

  const resolvePlaceholders = (text, valueMap) => {
    if (!text) return '';
    return text.replace(/\{([A-Z0-9_]+)\}/g, (match, key) =>
      key in valueMap ? valueMap[key] : match
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kira-sozlesmesi')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>
              <FileTextOutlined /> Kira Sözleşmesi
              {existingSozlesme && <Tag color="green" style={{ marginLeft: 8 }}>Mevcut Sözleşme</Tag>}
            </Title>
          </Space>
        </Col>
      </Row>

      {kiralikData && (
        <Card title="Kiralık Kaydı Bilgileri" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Kiraya Veren">{kiralikData.kirayaVeren || '-'}</Descriptions.Item>
            <Descriptions.Item label="Kiralayan">{kiralikData.kiralayanAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="Emlak Tipi">{kiralikData.emlakTipiAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="İl">{kiralikData.ilAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="İlçe">{kiralikData.ilceAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="Aylık Tutar">
              {kiralikData.aylikTutar
                ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(kiralikData.aylikTutar)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Kira Tarihi">
              {kiralikData.kiraTarihi ? dayjs(kiralikData.kiraTarihi).format('DD.MM.YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Bitiş Tarihi">
              {kiralikData.kiraBitisTarihi ? dayjs(kiralikData.kiraBitisTarihi).format('DD.MM.YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color="blue">{kiralikData.kiralikDurum || '-'}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card title="Sözleşme Bilgileri" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="sozlesmeNo"
                label="Sözleşme No"
                rules={[{ required: true, message: 'Sözleşme no zorunludur' }]}
              >
                <Input placeholder="KS-2024-001" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            Standart Maddeler
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              ({selectedMaddeler.length} seçili)
            </Text>
          </Divider>

          {maddelerState.length === 0 && (
            <Text type="secondary">Standart madde bulunamadı</Text>
          )}

          {maddelerState.map((madde) => (
            <Card
              key={madde.id}
              size="small"
              style={{
                marginBottom: 8,
                borderRadius: 6,
                borderColor: madde.selected ? '#1677ff' : '#d9d9d9',
                background: madde.selected ? '#f0f7ff' : '#fff',
              }}
              bodyStyle={{ padding: '8px 12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  checked={madde.selected}
                  onChange={() => toggleSelect(madde.id)}
                />
                <Text
                  strong
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => toggleExpand(madde.id)}
                >
                  {madde.maddeBaslik}
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={madde.expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                  onClick={() => toggleExpand(madde.id)}
                />
              </div>
              {madde.expanded && (
                <div style={{ marginTop: 8 }}>
                  <TextArea
                    rows={4}
                    value={madde.customIcerik}
                    onChange={(e) => updateCustomIcerik(madde.id, e.target.value)}
                    placeholder="Madde içeriği..."
                    style={{ fontSize: 13 }}
                  />
                  {kiralikData && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 3 }}>Çözümlenmiş Görünüm:</Text>
                      <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#333' }}>
                        {resolvePlaceholders(madde.customIcerik, buildKiraValueMap(kiralikData))}
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}

          <Divider orientation="left">Ek Maddeler</Divider>
          <Form.Item name="ekMaddeler" label="Ek Maddeler">
            <TextArea rows={6} placeholder="Ek sözleşme maddelerini buraya girin..." />
          </Form.Item>
        </Card>

        <Row justify="end">
          <Space>
            <Button onClick={() => navigate('/kira-sozlesmesi')}>İptal</Button>
            <Button
              icon={<EyeOutlined />}
              onClick={handleAc}
              loading={acLoading}
            >
              Aç
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleWordIndir}
              loading={wordLoading}
            >
              Kaydet ve İndir
            </Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {existingSozlesme ? 'Güncelle' : 'Sözleşme Oluştur'}
            </Button>
          </Space>
        </Row>
      </Form>

      <Modal
        title="Sözleşme Önizleme"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>Kapat</Button>,
        ]}
        width={700}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selectedMaddeler.map((m, i) => (
            <div key={m.id} style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {i + 1}. {m.maddeBaslik}
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                {resolvePlaceholders(m.customIcerik || m.maddeIcerik, buildKiraValueMap(kiralikData))}
              </Text>
            </div>
          ))}
          {form.getFieldValue('ekMaddeler') && (
            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Ek Maddeler:</Text>
              <Text style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                {form.getFieldValue('ekMaddeler')}
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default KiraSozlesmeForm;
